import {
  DOCUMENT_CATEGORIES, MODULE_TO_DOC_CATEGORY,
} from "../data/documentsData";

/* ════════════════════════════════════════════════════════════════════════
   DOCUMENT UTILS — pure helpers for the Documents & Admin Vault.
   All functions are side-effect free; state writes happen in the provider.
   ════════════════════════════════════════════════════════════════════════ */

const DUE_SOON_DAYS = 7;

function daysUntil(isoDate) {
  if (!isoDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(isoDate);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

export function isDueSoon(doc, withinDays = DUE_SOON_DAYS) {
  const refDate = doc.dueDate || doc.renewalDate || doc.reminderDate;
  if (!refDate) return false;
  const d = daysUntil(refDate);
  return d !== null && d >= 0 && d <= withinDays;
}

export function isOverdue(doc) {
  const refDate = doc.dueDate || doc.renewalDate;
  if (!refDate || doc.status === "Archived" || doc.status === "Filed") return false;
  const d = daysUntil(refDate);
  return d !== null && d < 0;
}

/* Normalise a Quick Capture entry into a document-shaped object. */
export function normaliseCaptureToDocument(capture, opts = {}) {
  const firstAttachment = (capture.attachments || [])[0] || null;
  const firstImage = (capture.attachments || []).find(a => a.kind === "image") || null;
  const suggested = MODULE_TO_DOC_CATEGORY[capture.module] || "Other";
  return {
    title: opts.title || capture.title || "Captured note",
    category: opts.category || suggested,
    type: opts.type || (firstImage ? "photo" : firstAttachment ? "file" : "note"),
    source: "Quick Capture",
    fileName: firstAttachment?.name || "",
    fileType: firstAttachment?.mime || "",
    attachmentData: firstAttachment?.dataUrl || null,
    thumbnailData: firstImage?.dataUrl || null,
    linkedCaptureId: capture.id,
    linkedTaskId: capture.relatedIds?.taskId || null,
    assignedTo: opts.assignedTo || capture.extracted?.person || null,
    relatedPerson: opts.relatedPerson || null,
    dueDate: opts.dueDate || capture.extracted?.date || null,
    renewalDate: opts.renewalDate || null,
    reminderDate: opts.reminderDate || null,
    status: opts.status || "Inbox",
    notes: opts.notes || capture.text || "",
    tags: opts.tags || [],
  };
}

export function getDocumentsByCategory(documents = [], category) {
  if (!category || category === "All") return documents;
  return (documents || []).filter(d => d.category === category);
}

export function getDocumentsDueSoon(documents = [], withinDays = DUE_SOON_DAYS) {
  return (documents || [])
    .filter(d => d.status !== "Archived" && isDueSoon(d, withinDays))
    .sort((a, b) => {
      const ad = a.dueDate || a.renewalDate || a.reminderDate || "";
      const bd = b.dueDate || b.renewalDate || b.reminderDate || "";
      return ad.localeCompare(bd);
    });
}

export function getDocumentsNeedingAction(documents = []) {
  return (documents || []).filter(d => d.status === "Needs action" || d.status === "Inbox" || isOverdue(d));
}

export function getRecentDocuments(documents = [], limit = 5) {
  return [...(documents || [])]
    .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""))
    .slice(0, limit);
}

export function getDocumentSummary(documents = []) {
  const total = documents.length;
  const dueSoon = getDocumentsDueSoon(documents).length;
  const needsAction = getDocumentsNeedingAction(documents).length;
  const filed = documents.filter(d => d.status === "Filed").length;
  const archived = documents.filter(d => d.status === "Archived").length;
  const byCategory = documents.reduce((acc, d) => {
    acc[d.category] = (acc[d.category] || 0) + 1;
    return acc;
  }, {});
  return { total, dueSoon, needsAction, filed, archived, byCategory };
}

export function getDocumentTypeIcon(doc) {
  if (!doc) return "FileText";
  const cat = DOCUMENT_CATEGORIES.find(c => c.id === doc.category);
  return cat?.icon || "FileText";
}

/* Build the metadata for a household task created as a document reminder.
   Caller passes it to `addTask` in the provider. */
export function createDocumentReminderTask(doc, opts = {}) {
  return {
    title: `Reminder: ${doc.title}`,
    description: doc.notes || "",
    category: "Documents",
    assignedTo: opts.assignedTo || doc.assignedTo || null,
    dueDate: opts.dueDate || doc.reminderDate || doc.dueDate || doc.renewalDate || null,
    status: "Needs action",
    priority: "medium",
    source: "documents-reminder",
    relatedFile: doc.fileName || null,
    notes: `Linked document: ${doc.title}`,
  };
}

export function searchDocuments(documents = [], query = "") {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return documents;
  return (documents || []).filter(d => {
    const haystack = [
      d.title, d.notes, d.category, d.fileName, d.assignedTo, d.relatedPerson,
      ...(d.tags || []),
    ].filter(Boolean).join(" ").toLowerCase();
    return haystack.includes(q);
  });
}

export const DUE_SOON_WINDOW_DAYS = DUE_SOON_DAYS;
export { daysUntil };

/* ════════════════════════════════════════════════════════════════════════
   RENEWAL RADAR — surfaces upcoming renewal / due / reminder dates from
   documents (and optionally tasks) so the household sees them coming.
   ════════════════════════════════════════════════════════════════════════ */

/* Pick the most relevant date for a document.
   Priority: renewalDate → dueDate → reminderDate. */
export function getRadarDate(doc) {
  return doc.renewalDate || doc.dueDate || doc.reminderDate || null;
}

/* Translate days-until → urgency band used for badge colour + sort. */
export function getRenewalUrgency(daysLeft) {
  if (daysLeft === null || daysLeft === undefined) return null;
  if (daysLeft < 0)      return { id: "overdue",  label: "Overdue",      rank: 0, chip: "bg-rose-600 text-white",                              row: "bg-rose-50 dark:bg-rose-900/15 border-rose-200 dark:border-rose-800/60",     text: "text-rose-700 dark:text-rose-300" };
  if (daysLeft <= 7)     return { id: "thisWeek", label: "Due this week",rank: 1, chip: "bg-orange-500 text-white",                            row: "bg-orange-50 dark:bg-orange-900/15 border-orange-200 dark:border-orange-800/60", text: "text-orange-700 dark:text-orange-300" };
  if (daysLeft <= 30)    return { id: "thisMonth",label: "Due this month",rank: 2,chip: "bg-amber-500 text-white",                             row: "bg-amber-50 dark:bg-amber-900/15 border-amber-200 dark:border-amber-800/60",  text: "text-amber-700 dark:text-amber-300" };
  if (daysLeft <= 90)    return { id: "comingUp", label: "Coming up",    rank: 3, chip: "bg-sky-500 text-white",                              row: "bg-sky-50 dark:bg-sky-900/15 border-sky-200 dark:border-sky-800/60",          text: "text-sky-700 dark:text-sky-300" };
  return { id: "later", label: "Later", rank: 4, chip: "bg-slate-400 text-white", row: "bg-slate-50 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700", text: "text-slate-700 dark:text-slate-300" };
}

const RADAR_INCLUDE_STATUSES = new Set(["Inbox", "Filed", "Needs action", "Reminder set"]);

/* Return all radar-eligible items from documents (and optional tasks).
   Each entry: { id, sourceId, source, title, category, dueDate, daysLeft,
                 urgency, doc (if from documents), task (if from tasks),
                 hasReminder, assignedTo, relatedPerson }.
*/
export function getRenewalRadarItems(documents = [], tasks = [], opts = {}) {
  const items = [];

  (documents || []).forEach(d => {
    if (!RADAR_INCLUDE_STATUSES.has(d.status)) return;
    const date = getRadarDate(d);
    if (!date) return;
    const daysLeft = daysUntil(date);
    const urgency = getRenewalUrgency(daysLeft);
    if (!urgency) return;
    items.push({
      id: `doc-${d.id}`,
      sourceId: d.id,
      source: "document",
      title: d.title,
      category: d.category,
      assignedTo: d.assignedTo,
      relatedPerson: d.relatedPerson,
      dueDate: date,
      daysLeft,
      urgency,
      hasReminder: !!(d.reminderDate || d.linkedTaskId || d.status === "Reminder set"),
      doc: d,
    });
  });

  if (opts.includeTasks) {
    (tasks || []).forEach(t => {
      const date = t.dueDate || t.reminderDate;
      if (!date || t.status === "Done" || t.status === "Complete" || t.status === "Cancelled") return;
      // Only Documents-style reminders to avoid flooding the radar.
      if (t.category !== "Documents") return;
      const daysLeft = daysUntil(date);
      const urgency = getRenewalUrgency(daysLeft);
      if (!urgency) return;
      items.push({
        id: `task-${t.id}`,
        sourceId: t.id,
        source: "task",
        title: t.title,
        category: t.category,
        assignedTo: t.assignedTo || null,
        dueDate: date,
        daysLeft,
        urgency,
        hasReminder: true,
        task: t,
      });
    });
  }

  return sortRenewalsByDate(items);
}

export function sortRenewalsByDate(items) {
  return [...(items || [])].sort((a, b) => {
    const ra = a.urgency?.rank ?? 99;
    const rb = b.urgency?.rank ?? 99;
    if (ra !== rb) return ra - rb;
    return (a.dueDate || "").localeCompare(b.dueDate || "");
  });
}

/* Quick selector for the compact widgets. */
export function getNextRenewalItems(documents = [], tasks = [], limit = 3) {
  return getRenewalRadarItems(documents, tasks).slice(0, limit);
}

/* Counts by band — used by compact summary lines. */
export function getRenewalRadarSummary(documents = [], tasks = []) {
  const items = getRenewalRadarItems(documents, tasks);
  const counts = { overdue: 0, thisWeek: 0, thisMonth: 0, comingUp: 0, later: 0 };
  items.forEach(it => { counts[it.urgency.id] = (counts[it.urgency.id] || 0) + 1; });
  return {
    total: items.length,
    counts,
    next: items[0] || null,
    hasOverdue: counts.overdue > 0,
    dueSoon: counts.overdue + counts.thisWeek + counts.thisMonth,
  };
}

/* ════════════════════════════════════════════════════════════════════════
   RENEWAL PREP — "ARA started prepping…" automation logic.
   When a reminder date passes OR the renewal sits inside a category-specific
   prep window, surface a gentle nudge on Today + Wall Display + Documents.
   ════════════════════════════════════════════════════════════════════════ */

/* Category default prep window (days). Tuned so big things get a head start. */
const CATEGORY_PREP_WINDOW_DAYS = {
  Bills:     7,
  Receipts:  0,
  School:    14,
  Medical:   14,
  Insurance: 30,
  IDs:       42, // passports / licences — these take weeks
  House:     30,
  Vehicle:   14,
  Tax:       30,
  Recipes:   0,
  Other:     14,
};

/* Title / tag overrides — applied if the category default needs a nudge. */
const TITLE_PREP_HINTS = [
  { match: /\b(passport|id\s+renewal|driver'?s?\s+licence|driver'?s?\s+license)\b/i, days: 42, action: "Start passport renewal prep" },
  { match: /\b(wof|warrant\s+of\s+fitness)\b/i,                                       days: 14, action: "Book WOF this week" },
  { match: /\b(rego|registration\s+renewal)\b/i,                                      days: 14, action: "Renew vehicle registration" },
  { match: /\b(insurance|policy\s+renewal)\b/i,                                       days: 30, action: "Review renewal before it rolls over" },
  { match: /\b(prescription|repeat|refill)\b/i,                                       days: 14, action: "Request repeat before you run out" },
  { match: /\b(school\s+fee|fees)\b/i,                                                days: 14, action: "Confirm payment plan or due date" },
  { match: /\b(permission\s+slip|consent\s+form)\b/i,                                 days: 7,  action: "Sign and return" },
  { match: /\b(tax|ird|gst|return)\b/i,                                               days: 30, action: "Prep tax documents" },
  { match: /\b(subscription)\b/i,                                                     days: 14, action: "Decide renew or cancel" },
  { match: /\b(power|electricity|water|internet|phone|gas)\s+bill\b/i,                days: 7,  action: "Schedule the payment" },
];

/* Suggested next-action copy when no title hint matches. */
const CATEGORY_DEFAULT_ACTION = {
  Bills:     "Schedule the payment",
  School:    "Sort it with the kids",
  Medical:   "Book the appointment",
  Insurance: "Review renewal before it rolls over",
  IDs:       "Start the renewal early",
  House:     "Plan the next step",
  Vehicle:   "Get the booking in",
  Tax:       "Prep tax documents",
  Other:     "Take the next small step",
};

export function getRenewalPrepWindowDays(doc) {
  if (!doc) return 14;
  // Title / tag hints win when they match.
  const haystack = [doc.title, ...(doc.tags || [])].filter(Boolean).join(" ");
  for (const hint of TITLE_PREP_HINTS) {
    if (hint.match.test(haystack)) return hint.days;
  }
  return CATEGORY_PREP_WINDOW_DAYS[doc.category] ?? 14;
}

export function getRenewalPrepAction(doc) {
  const haystack = [doc.title, ...(doc.tags || [])].filter(Boolean).join(" ");
  for (const hint of TITLE_PREP_HINTS) {
    if (hint.match.test(haystack) && hint.action) return hint.action;
  }
  return CATEGORY_DEFAULT_ACTION[doc.category] || "Take the next small step";
}

/* Decide whether this renewal is "ready to prep" right now. */
export function shouldStartRenewalPrep(doc, opts = {}) {
  if (!doc) return false;
  if (doc.status === "Archived") return false;
  // Respect snooze.
  if (doc.snoozedUntil) {
    const snoozeDays = daysUntil(doc.snoozedUntil);
    if (snoozeDays !== null && snoozeDays >= 0) return false;
  }
  const refDate = doc.renewalDate || doc.dueDate || doc.reminderDate;
  if (!refDate) return false;
  const left = daysUntil(refDate);
  if (left === null) return false;

  // Reminder reached or passed → always prep.
  if (doc.reminderDate) {
    const reminderLeft = daysUntil(doc.reminderDate);
    if (reminderLeft !== null && reminderLeft <= 0) return true;
  }
  // Within category prep window.
  const window = opts.windowOverride ?? getRenewalPrepWindowDays(doc);
  return left <= window && left >= -14; // include up to 14d overdue
}

/* Friendly message used by Today + Wall + Documents radar. */
export function getRenewalPrepMessage(doc) {
  if (!doc) return "";
  const refDate = doc.renewalDate || doc.dueDate || doc.reminderDate;
  const left = daysUntil(refDate);
  const window = getRenewalPrepWindowDays(doc);

  if (left === null) return "ARA suggests starting this when you have a moment.";
  if (left < 0) {
    const abs = Math.abs(left);
    return `This was due ${abs} day${abs === 1 ? "" : "s"} ago — let's gently get it across the line.`;
  }
  if (left === 0) return "This is due today. ARA suggests starting now.";
  if (window >= 30) {
    return `${doc.title.includes("expir") || doc.title.toLowerCase().includes("passport") ? "Expires" : "Due"} in ${left} day${left === 1 ? "" : "s"}. It may take several weeks, so ARA suggests starting now.`;
  }
  return `Due in ${left} day${left === 1 ? "" : "s"}. Want to get ahead of it?`;
}

/* Build a task payload (no provider write — caller passes to addTask). */
export function buildRenewalPrepTask(doc) {
  const action = getRenewalPrepAction(doc);
  return {
    title: `${action}: ${doc.title}`,
    description: doc.notes || "",
    category: "Documents",
    assignedTo: doc.assignedTo || null,
    dueDate: doc.reminderDate || doc.dueDate || doc.renewalDate || null,
    status: "Needs action",
    priority: "medium",
    source: "renewal-prep",
    linkedDocumentId: doc.id,
    relatedFile: doc.fileName || null,
    notes: `Renewal prep for: ${doc.title}`,
  };
}

const COMPLETED_TASK_STATUSES = new Set(["Done", "Complete", "Completed", "Cancelled"]);

/* True if this document already has a live renewal-prep task or linked task. */
export function hasExistingPrepTask(doc, tasks = []) {
  if (!doc) return false;
  // Linked task still alive
  if (doc.linkedTaskId) {
    const linked = (tasks || []).find(t => t.id === doc.linkedTaskId);
    if (linked && !COMPLETED_TASK_STATUSES.has(linked.status)) return true;
  }
  // Any source: "renewal-prep" pointing at this doc
  return (tasks || []).some(
    t => t.source === "renewal-prep"
      && t.linkedDocumentId === doc.id
      && !COMPLETED_TASK_STATUSES.has(t.status)
  );
}

/* Build the prep nudges list for Today + Wall + Docs radar.
   - Excludes docs that already have a prep task (no spam).
   - Excludes snoozed docs.
   - Sorted by days-until-due (soonest first, overdue first). */
export function getRenewalsReadyToPrep(documents = [], tasks = [], opts = {}) {
  const limit = opts.limit ?? 3;
  const out = [];
  (documents || []).forEach(d => {
    if (!shouldStartRenewalPrep(d)) return;
    if (hasExistingPrepTask(d, tasks)) return;
    const refDate = d.renewalDate || d.dueDate || d.reminderDate;
    const left = daysUntil(refDate);
    out.push({
      id: `prep-${d.id}`,
      doc: d,
      refDate,
      daysLeft: left,
      message: getRenewalPrepMessage(d),
      action: getRenewalPrepAction(d),
      window: getRenewalPrepWindowDays(d),
    });
  });
  out.sort((a, b) => (a.daysLeft ?? 999) - (b.daysLeft ?? 999));
  return limit ? out.slice(0, limit) : out;
}

/* Counts for compact summary lines. */
export function getRenewalPrepSummary(documents = [], tasks = []) {
  const all = getRenewalsReadyToPrep(documents, tasks, { limit: null });
  const overdue = all.filter(p => (p.daysLeft ?? 0) < 0).length;
  return {
    total: all.length,
    overdue,
    next: all[0] || null,
  };
}

/* Snooze helpers — produce the patch you pass into updateDocument. */
export function buildSnoozePatch(days) {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  t.setDate(t.getDate() + days);
  return {
    snoozedUntil: t.toISOString().slice(0, 10),
    lastSnoozedAt: new Date().toISOString(),
  };
}
